import { signal, effect, untracked } from '@preact/signals-core';
import {
  getAuthHeaders,
  assetsListSignal,
  customersSignal,
  sitesSignal,
  activeRoleSignal,
  refreshAssets
} from './assets';
import { escapeHtml } from '../utils/sanitize';

export interface PendingWorkCounts {
  pendingSuggestionsCount: number;
  pendingSuggestionsItems: any[];
  pendingPartsCount: number;
  pendingPartsItems: any[];
  resolvedTicketsCount: number;
  resolvedTicketsItems: any[];
  approvedPartsCount: number;
  approvedPartsItems: any[];
  activeTicketsCount: number;
  activeTicketsItems: any[];
  approvedSuggestionsCount: number;
  approvedSuggestionsItems: any[];
}

export const dashboardStatsSignal = signal<any | null>(null);
export const recentAuditSignal = signal<any[]>([]);
export const dashboardLoadingSignal = signal<boolean>(false);
export const pendingWorkSignal = signal<PendingWorkCounts | null>(null);

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const datePart = d.toLocaleDateString('en-GB'); // DD/MM/YYYY
  const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${datePart} ${timePart}`;
}

export async function fetchPendingWorkData(): Promise<PendingWorkCounts> {
  const h = getAuthHeaders();
  const role = activeRoleSignal.value;
  const isManager = role === 'superadmin' || role === 'admin' || role === 'manager';
  const isTech = role === 'technician';

  let pendingSuggestionsCount = 0;
  let pendingSuggestionsItems: any[] = [];
  let pendingPartsCount = 0;
  let pendingPartsItems: any[] = [];
  let resolvedTicketsCount = 0;
  let resolvedTicketsItems: any[] = [];

  let approvedPartsCount = 0;
  let approvedPartsItems: any[] = [];
  let activeTicketsCount = 0;
  let activeTicketsItems: any[] = [];
  let approvedSuggestionsCount = 0;
  let approvedSuggestionsItems: any[] = [];

  try {
    if (isManager) {
      // 1. Ticket Suggestions (Priority for Managers)
      const suggRes = await fetch('/api/v1/ticket-suggestions?status=pending&page=1&pageSize=5', { headers: h });
      if (suggRes.ok) {
        const data = await suggRes.json();
        pendingSuggestionsItems = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        pendingSuggestionsCount = data.total !== undefined ? data.total : pendingSuggestionsItems.length;
      }

      // 2. Parts Approvals (Awaiting manager or admin approval)
      const partsRes = await fetch('/api/v1/parts-recommendations', { headers: h });
      if (partsRes.ok) {
        const partsData = await partsRes.json();
        const allParts = Array.isArray(partsData) ? partsData : (partsData.items || []);
        pendingPartsItems = allParts.filter((p: any) =>
          p.status === 'requested' ||
          p.status === 'awaiting_approval' ||
          p.status === 'pending' ||
          p.status === 'manager_approved'
        );
        pendingPartsCount = pendingPartsItems.length;
      }

      // 3. Resolved Reviews
      const tixRes = await fetch('/api/v1/tickets?status=resolved&page=1&pageSize=5', { headers: h });
      if (tixRes.ok) {
        const tixData = await tixRes.json();
        resolvedTicketsItems = Array.isArray(tixData.items) ? tixData.items : (Array.isArray(tixData) ? tixData : []);
        const totalHeader = tixRes.headers.get('X-Total-Count');
        resolvedTicketsCount = totalHeader ? Number(totalHeader) : (tixData.total !== undefined ? tixData.total : resolvedTicketsItems.length);
      }
    } else if (isTech) {
      // 1. Approved Parts to Install (Priority for Technicians)
      const partsRes = await fetch('/api/v1/parts-recommendations', { headers: h });
      if (partsRes.ok) {
        const partsData = await partsRes.json();
        const allParts = Array.isArray(partsData) ? partsData : (partsData.items || []);
        approvedPartsItems = allParts.filter((p: any) =>
          p.status === 'approved' ||
          p.status === 'admin_approved' ||
          p.status === 'manager_approved' ||
          p.status === 'ready_for_dispatch'
        );
        approvedPartsCount = approvedPartsItems.length;
      }

      // 2. Active Tickets assigned to technician
      const activeTixRes = await fetch('/api/v1/tickets?status=in_progress&page=1&pageSize=5', { headers: h });
      if (activeTixRes.ok) {
        const tixData = await activeTixRes.json();
        activeTicketsItems = Array.isArray(tixData.items) ? tixData.items : (Array.isArray(tixData) ? tixData : []);
        const totalHeader = activeTixRes.headers.get('X-Total-Count');
        activeTicketsCount = totalHeader ? Number(totalHeader) : (tixData.total !== undefined ? tixData.total : activeTicketsItems.length);
      }

      // 3. Approved Suggested Tickets
      const suggRes = await fetch('/api/v1/ticket-suggestions?status=approved&page=1&pageSize=5', { headers: h });
      if (suggRes.ok) {
        const data = await suggRes.json();
        approvedSuggestionsItems = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
        approvedSuggestionsCount = data.total !== undefined ? data.total : approvedSuggestionsItems.length;
      }
    }
  } catch (e) {
    console.error('Failed to fetch pending work data:', e);
  }

  return {
    pendingSuggestionsCount,
    pendingSuggestionsItems,
    pendingPartsCount,
    pendingPartsItems,
    resolvedTicketsCount,
    resolvedTicketsItems,
    approvedPartsCount,
    approvedPartsItems,
    activeTicketsCount,
    activeTicketsItems,
    approvedSuggestionsCount,
    approvedSuggestionsItems
  };
}

export async function refreshDashboard() {
  dashboardLoadingSignal.value = true;
  try {
    const h = getAuthHeaders();

    // Fetch pending work items requiring attention
    pendingWorkSignal.value = await fetchPendingWorkData();

    // Fetch latest audit logs (last 10 entries)
    const auditRes = await fetch('/api/v1/audit-logs?limit=10&offset=0', { headers: h });
    if (auditRes.ok) {
      recentAuditSignal.value = await auditRes.json();
    }

    // If assets are not loaded yet, refresh them
    if (!assetsListSignal.value || assetsListSignal.value.length === 0) {
      await refreshAssets();
    }

    const assets = assetsListSignal.value || [];
    const activeCount = assets.filter(a => a.status === 'active').length;
    const errorCount = assets.filter(a => a.status === 'error').length;
    const retiredCount = assets.filter(a => a.status === 'retired').length;

    dashboardStatsSignal.value = {
      totalAssets: assets.length,
      activeCount,
      errorCount,
      retiredCount,
      customerCount: (customersSignal.value || []).length,
      siteCount: (sitesSignal.value || []).length,
    };
  } catch (e) {
    console.error('Failed to load dashboard data', e);
  } finally {
    dashboardLoadingSignal.value = false;
  }
}

export function renderDashboardPage(): string {
  const stats = dashboardStatsSignal.value;
  const recentLogs = recentAuditSignal.value || [];
  const loading = dashboardLoadingSignal.value;
  const role = activeRoleSignal.value;
  const isManagerPlus = role === 'superadmin' || role === 'admin' || role === 'manager';
  const assets = assetsListSignal.value || [];

  // Compute attention items
  const errorAssets = assets.filter(a => a.status === 'error');

  if (loading || !stats) {
    return `
      <s-page heading="Operations Dashboard" inlineSize="large">
        <s-stack gap="base" alignment="center" style="padding:5rem 0;">
          <s-spinner size="large"></s-spinner>
          <s-text tone="subdued">Loading fleet status...</s-text>
        </s-stack>
      </s-page>
    `;
  }

  return `
    <s-page heading="Operations Dashboard" subtitle="Fleet health and operational status at a glance." inlineSize="large">
      
      <!-- KPI Cards Row -->
      <s-section>
        <s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="base">
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="small">
              <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Total Fleet</s-text>
              <s-heading element="h2" style="font-size:1.75rem;font-weight:700;">${stats.totalAssets}</s-heading>
              <s-text tone="subdued" style="font-size:0.8rem;">Across ${stats.customerCount} customers</s-text>
            </s-stack>
          </s-box>
          
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="small">
              <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Active</s-text>
              <s-heading element="h2" style="font-size:1.75rem;font-weight:700;color:var(--p-color-text-success, #008060);">${stats.activeCount}</s-heading>
              <s-text tone="subdued" style="font-size:0.8rem;">Polling normally</s-text>
            </s-stack>
          </s-box>
          
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="small">
              <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Errors</s-text>
              <s-heading element="h2" style="font-size:1.75rem;font-weight:700;color:${stats.errorCount > 0 ? 'var(--p-color-text-critical, #d72c0d)' : 'var(--p-color-text, #202223)'};">${stats.errorCount}</s-heading>
              <s-text tone="subdued" style="font-size:0.8rem;">${stats.errorCount > 0 ? 'Needs attention' : 'All clear'}</s-text>
            </s-stack>
          </s-box>
          
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-stack gap="small">
              <s-text tone="subdued" style="font-size:0.75rem;text-transform:uppercase;font-weight:600;">Sites</s-text>
              <s-heading element="h2" style="font-size:1.75rem;font-weight:700;">${stats.siteCount}</s-heading>
              <s-text tone="subdued" style="font-size:0.8rem;">Active locations</s-text>
            </s-stack>
          </s-box>
        </s-grid>
      </s-section>
      
      <!-- Two Column Layout: Alerts + Quick Actions -->
      <s-grid gridTemplateColumns="2fr 1fr" gap="base" style="margin-top:1.5rem;">
        
        <!-- Left: Attention Items (Role-Based Pending Work) -->
        <s-box padding="none" background="surface" border="base" borderRadius="base">
          <div style="padding:1rem;border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);display:flex;justify-content:space-between;align-items:center;">
            <s-stack direction="inline" gap="small" align="center">
              <s-heading element="h3">Items Requiring Your Attention</s-heading>
              ${(() => {
                const p = pendingWorkSignal.value;
                if (!p) return '';
                const total = isManagerPlus 
                  ? (p.pendingSuggestionsCount + p.pendingPartsCount + p.resolvedTicketsCount)
                  : (p.approvedPartsCount + p.activeTicketsCount + p.approvedSuggestionsCount);
                return total > 0 ? `<s-badge tone="attention">${total} Pending</s-badge>` : `<s-badge tone="success">All Clear</s-badge>`;
              })()}
            </s-stack>
          </div>
          <div style="padding:1rem;">
            ${(() => {
              const p = pendingWorkSignal.value;
              if (isManagerPlus) {
                const hasWork = p && (p.pendingSuggestionsCount > 0 || p.pendingPartsCount > 0 || p.resolvedTicketsCount > 0);
                if (!hasWork && errorAssets.length === 0) {
                  return `
                    <div style="padding:2rem;text-align:center;">
                      <s-text tone="subdued">✓ No pending items requiring attention. All workflows up to date.</s-text>
                    </div>
                  `;
                }
                return `
                  <s-stack gap="base">
                    <!-- 1. Ticket Suggestions (Priority for Managers) -->
                    ${p && p.pendingSuggestionsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #d72c0d;background:#fff8f6;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Ticket Suggestions</s-text>
                            <s-badge tone="critical">${p.pendingSuggestionsCount} Pending</s-badge>
                          </s-stack>
                          <a href="#ticket-suggestions" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">Review Suggestions →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.pendingSuggestionsItems.slice(0, 3).map(s => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>${escapeHtml(s.modelNameRaw || s.serialNumberRaw || 'Device')}</strong>: ${escapeHtml(s.description ? s.description.slice(0, 85) : 'Technician ticket suggestion awaiting approval')}
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}

                    <!-- 2. Parts Approvals -->
                    ${p && p.pendingPartsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #b87503;background:#fffbf0;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Parts Approvals</s-text>
                            <s-badge tone="warning">${p.pendingPartsCount} Pending</s-badge>
                          </s-stack>
                          <a href="#parts-approvals" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">Approve Parts →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.pendingPartsItems.slice(0, 3).map(pt => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>Part Request</strong>: ${escapeHtml(pt.partName || pt.consumableId || 'Replacement Part')} (Qty: ${pt.quantity || 1})
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}

                    <!-- 3. Resolved Reviews -->
                    ${p && p.resolvedTicketsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #008060;background:#f4fbf7;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Resolved Ticket Reviews</s-text>
                            <s-badge tone="success">${p.resolvedTicketsCount} Resolved</s-badge>
                          </s-stack>
                          <a href="#tickets" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">Review Tickets →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.resolvedTicketsItems.slice(0, 3).map(t => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>Ticket #${escapeHtml(t.ticketNumber)}</strong>: Marked as resolved, awaiting manager closeout
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}

                    <!-- Fleet Polling Errors (if any) -->
                    ${errorAssets.length > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #d72c0d;background:#fff8f6;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-text style="font-weight:700;color:#202223;">Fleet Polling Failures</s-text>
                          <s-badge tone="critical">${errorAssets.length} Error(s)</s-badge>
                        </div>
                        <s-stack gap="extraSmall">
                          ${errorAssets.slice(0, 3).map(a => `
                            <div style="font-size:0.825rem;color:#454f5b;display:flex;justify-content:space-between;align-items:center;">
                              <span>• <strong>${escapeHtml(a.serialNumber)}</strong>: Polling offline</span>
                              <a href="#assets" style="color:var(--p-color-text-link,#008060);font-size:0.8rem;text-decoration:none;">View →</a>
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}
                  </s-stack>
                `;
              } else {
                // Technician View
                const hasWork = p && (p.approvedPartsCount > 0 || p.activeTicketsCount > 0 || p.approvedSuggestionsCount > 0);
                if (!hasWork) {
                  return `
                    <div style="padding:2rem;text-align:center;">
                      <s-text tone="subdued">✓ No active items assigned. You are all caught up!</s-text>
                    </div>
                  `;
                }
                return `
                  <s-stack gap="base">
                    <!-- 1. Approved Parts to Install (Priority for Technicians) -->
                    ${p && p.approvedPartsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #d72c0d;background:#fff8f6;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Approved Parts to Install</s-text>
                            <s-badge tone="critical">${p.approvedPartsCount} Approved</s-badge>
                          </s-stack>
                          <a href="#parts-approvals" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">Install Parts →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.approvedPartsItems.slice(0, 3).map(pt => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>Part Ready</strong>: ${escapeHtml(pt.partName || pt.consumableId || 'Approved Part')} ready to install
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}

                    <!-- 2. Active Tickets -->
                    ${p && p.activeTicketsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #b87503;background:#fffbf0;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Active Assigned Tickets</s-text>
                            <s-badge tone="warning">${p.activeTicketsCount} Active</s-badge>
                          </s-stack>
                          <a href="#tickets" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">Open Tickets →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.activeTicketsItems.slice(0, 3).map(t => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>Ticket #${escapeHtml(t.ticketNumber)}</strong>: ${escapeHtml(t.description ? t.description.slice(0, 75) : 'Assigned active ticket')}
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}

                    <!-- 3. Approved Suggested Tickets -->
                    ${p && p.approvedSuggestionsCount > 0 ? `
                      <div style="padding:0.85rem;border:1px solid var(--p-color-border-subdued,#e1e3e5);border-radius:8px;border-left:4px solid #008060;background:#f4fbf7;">
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem;">
                          <s-stack direction="inline" gap="small" align="center">
                            <s-text style="font-weight:700;color:#202223;">Approved Suggested Tickets</s-text>
                            <s-badge tone="success">${p.approvedSuggestionsCount} Approved</s-badge>
                          </s-stack>
                          <a href="#ticket-suggestions" style="color:var(--p-color-text-link,#008060);font-weight:600;font-size:0.85rem;text-decoration:none;">View Suggestions →</a>
                        </div>
                        <s-stack gap="extraSmall">
                          ${p.approvedSuggestionsItems.slice(0, 3).map(s => `
                            <div style="font-size:0.825rem;color:#454f5b;">
                              • <strong>Approved Suggestion</strong>: ${escapeHtml(s.modelNameRaw || s.serialNumberRaw || 'Suggestion')} converted to ticket
                            </div>
                          `).join('')}
                        </s-stack>
                      </div>
                    ` : ''}
                  </s-stack>
                `;
              }
            })()}
          </div>
        </s-box>
        
        <!-- Right: Quick Actions -->
        <s-box padding="base" background="surface" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading element="h3">Quick Actions</s-heading>
            <s-stack gap="small">
              <a href="#assets" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem;border-radius:6px;text-decoration:none;color:var(--p-color-text, #202223);transition:background 0.15s;" onmouseover="this.style.background='var(--p-color-bg-action-hover, rgba(0,0,0,0.05))'" onmouseout="this.style.background='transparent'">
                <s-icon type="inventory" size="base" style="color:var(--p-color-icon, #5c5f62);"></s-icon>
                <s-text>Asset Explorer</s-text>
              </a>
              ${isManagerPlus ? `
                <a href="#analytics" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem;border-radius:6px;text-decoration:none;color:var(--p-color-text, #202223);transition:background 0.15s;" onmouseover="this.style.background='var(--p-color-bg-action-hover, rgba(0,0,0,0.05))'" onmouseout="this.style.background='transparent'">
                  <s-icon type="chart-donut" size="base" style="color:var(--p-color-icon, #5c5f62);"></s-icon>
                  <s-text>P&L Analytics</s-text>
                </a>
                <a href="#contracts" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem;border-radius:6px;text-decoration:none;color:var(--p-color-text, #202223);transition:background 0.15s;" onmouseover="this.style.background='var(--p-color-bg-action-hover, rgba(0,0,0,0.05))'" onmouseout="this.style.background='transparent'">
                  <s-icon type="document" size="base" style="color:var(--p-color-icon, #5c5f62);"></s-icon>
                  <s-text>Contracts Portal</s-text>
                </a>
                <a href="#billing" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem;border-radius:6px;text-decoration:none;color:var(--p-color-text, #202223);transition:background 0.15s;" onmouseover="this.style.background='var(--p-color-bg-action-hover, rgba(0,0,0,0.05))'" onmouseout="this.style.background='transparent'">
                  <s-icon type="receipt" size="base" style="color:var(--p-color-icon, #5c5f62);"></s-icon>
                  <s-text>Internal Memos</s-text>
                </a>
              ` : ''}
              <a href="#tickets" style="display:flex;align-items:center;gap:0.75rem;padding:0.5rem;border-radius:6px;text-decoration:none;color:var(--p-color-text, #202223);transition:background 0.15s;" onmouseover="this.style.background='var(--p-color-bg-action-hover, rgba(0,0,0,0.05))'" onmouseout="this.style.background='transparent'">
                <s-icon type="alert-circle" size="base" style="color:var(--p-color-icon, #5c5f62);"></s-icon>
                <s-text>Support Tickets</s-text>
              </a>
            </s-stack>
          </s-stack>
        </s-box>
      </s-grid>
      
      <!-- Recent Activity Feed (Manager+) -->
      ${isManagerPlus ? `
        <s-section style="margin-top:1.5rem;">
          <s-box padding="none" background="surface" border="base" borderRadius="base">
            <div style="padding:1rem;border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);display:flex;justify-content:space-between;align-items:center;">
              <s-heading element="h3">Recent Activity</s-heading>
              <a href="#audit" style="color:var(--p-color-text-link, #008060);text-decoration:none;font-size:0.85rem;font-weight:600;">View All →</a>
            </div>
            <div style="padding:0.5rem 1rem;">
              ${recentLogs.length === 0 ? `
                <div style="padding:2rem;text-align:center;">
                  <s-text tone="subdued">No recent activity recorded.</s-text>
                </div>
              ` : `
                <s-stack gap="none">
                  ${recentLogs.slice(0, 8).map(log => {
    const tone = log.action === 'INSERT' ? 'success' : log.action === 'UPDATE' ? 'attention' : 'critical';
    const timeStr = formatDateTime(log.createdAt);
    return `
                      <div style="display:flex;align-items:center;gap:1rem;padding:0.6rem 0;border-bottom:1px solid var(--p-color-border-subdued, #e1e3e5);">
                        <s-badge tone="${tone}" style="min-width:60px;text-align:center;">${log.action}</s-badge>
                        <s-stack gap="none" style="flex:1;min-width:0;">
                          <s-text style="font-weight:500;font-size:0.875rem;">${log.tableName}</s-text>
                          <s-text tone="subdued" style="font-size:0.75rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">ID: ${log.recordId}</s-text>
                        </s-stack>
                        <s-text tone="subdued" style="font-size:0.75rem;white-space:nowrap;">${timeStr}</s-text>
                      </div>
                    `;
  }).join('')}
                </s-stack>
              `}
            </div>
          </s-box>
        </s-section>
      ` : ''}
      
    </s-page>
  `;
}

let _dashboardEffectDisposer: (() => void) | null = null;

export function setupDashboardPageListeners() {
  if (_dashboardEffectDisposer) {
    _dashboardEffectDisposer();
    _dashboardEffectDisposer = null;
  }

  _dashboardEffectDisposer = effect(() => {
    // Explicit signal subscriptions to trigger view update when dashboard data resolves
    dashboardLoadingSignal.value;
    dashboardStatsSignal.value;
    pendingWorkSignal.value;

    const slot = document.getElementById('main-content-slot');
    const route = window.location.hash || '#dashboard';
    const isDashboardRoute = route === '#dashboard' || route === '' || !route || route === '#';

    if (slot && isDashboardRoute) {
      untracked(() => {
        slot.innerHTML = renderDashboardPage();
        bindDashboardActionEvents();
      });
    }
  });

  bindDashboardActionEvents();
}

export function unmountDashboardPage(): void {
  if (_dashboardEffectDisposer) {
    _dashboardEffectDisposer();
    _dashboardEffectDisposer = null;
  }
}

function bindDashboardActionEvents() {
  const assets = assetsListSignal.value || [];

  // Error asset "View Details" buttons
  const errorAssets = assets.filter(a => a.status === 'error');
  errorAssets.slice(0, 5).forEach(a => {
    const btn = document.getElementById(`dash-view-error-${a.id}`);
    if (btn) {
      btn.addEventListener('click', () => {
        window.location.hash = `#machine-history?id=${encodeURIComponent(a.id)}`;
      });
    }
  });

  // Populate fleet map
  const mapEl = document.getElementById('dashboard-fleet-map') as any;
  if (mapEl && typeof mapEl.setMarkers === 'function') {
    const sites = sitesSignal.value || [];
    const markers = assets.map(a => {
      const site = sites.find(s => s.id === a.siteId);
      if (!site || site.latitude === undefined || site.longitude === undefined || site.latitude === null || site.longitude === null) return null;

      const latVal = typeof site.latitude === 'object' && site.latitude !== null ? (site.latitude as any).value : site.latitude;
      const lngVal = typeof site.longitude === 'object' && site.longitude !== null ? (site.longitude as any).value : site.longitude;

      if (latVal === undefined || lngVal === undefined || latVal === null || lngVal === null) return null;

      return {
        lat: Number(latVal),
        lng: Number(lngVal),
        label: a.serialNumber,
        id: a.id,
        status: a.status
      };
    }).filter(Boolean);
    mapEl.setMarkers(markers);
  }
}
